import type {
  AttachedFile,
  ContentBlock,
  OutputFailure,
  PreparingOutput,
  UploadRefusal,
  VaultSave,
} from '../types';

export const WIRE = {
  vaultSavedEvent: 'vault_file_saved',
  vaultSavedAction: 'VAULT_FILE_SAVED',
  outputFailedEvent: 'file_output_failed',
  outputFailedAction: 'FILE_OUTPUT_FAILED',
  taskStartedAction: 'TASK_STARTED',
  taskStartedOutputs: 'outputs',
  outputId: 'output_id',
  outputName: 'file_name',
  vaultFileId: 'vault_file_id',
  blockOutputId: 'output_id',
  blockVaultFileId: 'vault_file_id',
  saveSource: 'source',
  saveSourceUpload: 'upload',
  saveSourceOutput: 'output',
  refusalCode: 'code',
  refusalMessage: 'message',
  refusalMaxBytes: 'max_bytes',
  transcriptAttachments: 'attachments',
  transcriptBlocks: 'blocks',
  transcriptBlockOrder: 'block_order',
  fileRef: 'file_ref',
  fileName: 'file_name',
  blockId: 'block_id',
  taskId: 'task_id',
  contentType: 'content_type',
  sizeBytes: 'size_bytes',
} as const;

export const ORCHESTRATOR_CAPABILITIES: { readonly deleteFile: boolean } = { deleteFile: false };

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function nonEmpty(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

const VAULT_FILE_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;

export function readVaultFileId(value: unknown): string | undefined {
  return typeof value === 'string' && VAULT_FILE_ID.test(value) ? value : undefined;
}

function byteCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;
}

export function readUploadRefusal(status: number, body: unknown): UploadRefusal {
  const record = asRecord(body);
  const detail = record?.detail;
  const detailRecord = asRecord(detail);
  const message =
    nonEmpty(detail) ??
    nonEmpty(detailRecord?.[WIRE.refusalMessage]) ??
    `Failed to upload file: ${status.toString()}`;
  const code = nonEmpty(detailRecord?.[WIRE.refusalCode]) ?? nonEmpty(record?.[WIRE.refusalCode]);
  const limitBytes =
    byteCount(detailRecord?.[WIRE.refusalMaxBytes]) ?? byteCount(record?.[WIRE.refusalMaxBytes]);
  return {
    status,
    message,
    ...(code === undefined ? {} : { code }),
    ...(limitBytes === undefined ? {} : { limitBytes }),
  };
}

export function readAttachResponse(
  body: unknown,
  fallbackRef: string,
): { fileRef: string; vaultFileId?: string } {
  const record = asRecord(body);
  const vaultFileId = readVaultFileId(record?.[WIRE.vaultFileId]);
  return {
    fileRef: nonEmpty(record?.[WIRE.fileRef]) ?? fallbackRef,
    ...(vaultFileId === undefined ? {} : { vaultFileId }),
  };
}

export function readVaultSave(data: Record<string, unknown>): VaultSave | undefined {
  const vaultFileId = readVaultFileId(data[WIRE.vaultFileId]);
  if (vaultFileId === undefined) return undefined;
  const source = data[WIRE.saveSource];
  const fileRef = nonEmpty(data[WIRE.fileRef]);
  const outputId = nonEmpty(data[WIRE.outputId]);
  const blockId = nonEmpty(data[WIRE.blockId]);
  const name = nonEmpty(data[WIRE.outputName]);
  const isOutput =
    source === WIRE.saveSourceOutput ||
    (source !== WIRE.saveSourceUpload && (outputId !== undefined || blockId !== undefined));
  if (isOutput) {
    return {
      kind: 'output',
      vaultFileId,
      ...(outputId === undefined ? {} : { outputId }),
      ...(blockId === undefined ? {} : { blockId }),
      ...(name === undefined ? {} : { name }),
    };
  }
  return fileRef === undefined ? undefined : { kind: 'upload', fileRef, vaultFileId };
}

export function readPreparingOutputs(data: Record<string, unknown>): PreparingOutput[] {
  const taskId = nonEmpty(data[WIRE.taskId]);
  const outputs = data[WIRE.taskStartedOutputs];
  if (taskId === undefined || !Array.isArray(outputs)) return [];
  return outputs.flatMap((output: unknown): PreparingOutput[] => {
    const plainName = nonEmpty(output);
    if (plainName !== undefined) return [{ taskId, name: plainName.trim() }];
    const record = asRecord(output);
    const name = nonEmpty(record?.[WIRE.outputName]);
    if (name === undefined) return [];
    const outputId = nonEmpty(record?.[WIRE.outputId]);
    return [{ taskId, name: name.trim(), ...(outputId === undefined ? {} : { outputId }) }];
  });
}

export function readOutputFailure(data: Record<string, unknown>): OutputFailure | undefined {
  const name = nonEmpty(data[WIRE.outputName])?.trim() ?? '';
  const outputId = nonEmpty(data[WIRE.outputId]);
  if (name === '' && outputId === undefined) return undefined;
  const taskId = nonEmpty(data[WIRE.taskId]);
  return {
    name,
    ...(taskId === undefined ? {} : { taskId }),
    ...(outputId === undefined ? {} : { outputId }),
  };
}

export function readTranscriptFiles(raw: unknown, idPrefix: string): AttachedFile[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item: unknown, index): AttachedFile[] => {
    const record = asRecord(item);
    const name = nonEmpty(record?.[WIRE.fileName]);
    if (!record || name === undefined) return [];
    const fileRef = nonEmpty(record[WIRE.fileRef]);
    const vaultFileId = readVaultFileId(record[WIRE.vaultFileId]);
    return [
      {
        id: `${idPrefix}:${index.toString()}`,
        name,
        size: byteCount(record[WIRE.sizeBytes]) ?? 0,
        type: nonEmpty(record[WIRE.contentType]) ?? '',
        ...(fileRef === undefined ? {} : { fileRef }),
        ...(vaultFileId === undefined ? {} : { vaultFileId }),
      },
    ];
  });
}

function withRestoredExpiry(block: ContentBlock, now: number): ContentBlock {
  if (block.type !== 'file' || !block.payload) return block;
  const expiresAt = block.payload.expires_at;
  if (typeof expiresAt === 'string' && Number.isFinite(Date.parse(expiresAt))) return block;
  return { ...block, payload: { ...block.payload, expires_at: new Date(now).toISOString() } };
}

export function readTranscriptBlocks(
  raw: unknown,
  restoredAt: number | undefined,
  toBlock: (data: Record<string, unknown>) => ContentBlock | undefined,
): ContentBlock[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item: unknown): ContentBlock[] => {
    const record = asRecord(item);
    const block = record ? toBlock(record) : undefined;
    if (!block) return [];
    return [restoredAt === undefined ? block : withRestoredExpiry(block, restoredAt)];
  });
}

export function readBlockOrder(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const ids = raw.filter((id): id is string => typeof id === 'string' && id !== '');
  return ids.length ? ids : undefined;
}
